import React, { useState, useEffect, SyntheticEvent } from "react";
import {
  Button,
  Dropdown,
  DropdownProps,
  Grid,
  Label,
} from "semantic-ui-react";
import messages from "./messages";
import DatePicker from "react-datepicker";
import getDay from "date-fns/getDay";
import "react-datepicker/src/stylesheets/datepicker.scss";
import styles from "./styles.module.scss";
import moment from "moment";
import { fileDataFormat } from "../../utils/dateFormat";
import { TkrCusip, InstrumentParams, PortfolioEntry, Slice } from "../../types";
import InstrumentSelector from "../Instrument/InstrumentSelector";
import { Link } from "react-router-dom";
import { FormattedMessage } from "react-intl";

export interface Props {
  date: string;
  slices: string[];
  currSlice: string;
  urlBase: string;
  handleDateChange: (date: string, token: string | null) => void;
  token: string | null;
  handleSliceChange: (slice: Slice) => void;
  fetchInst: (params: InstrumentParams, token: string | null) => void;
  fetchReport: (reportDetails: Slice, token: string | null) => void;
  cusipTkrCollection: PortfolioEntry[];
  tabIndex: number;
  setTkrCusip: (tkrCusip: TkrCusip) => void;
}

const ReportPicker = ({
  date,
  slices,
  currSlice,
  urlBase,
  handleDateChange,
  token,
  handleSliceChange,
  cusipTkrCollection,
  fetchInst,
  fetchReport,
  tabIndex,
  setTkrCusip,
}: Props) => {
  const [valid, setValid] = useState(false);
  const [value, setValue] = useState(currSlice);
  const [pickDate, setPickDate] = useState(
    moment(date, fileDataFormat).toDate()
  );
  const [options, setOptions] = useState(
    [] as { key: string; value: string; text: string }[]
  );

  useEffect(() => {
    //Get available reports for today
    //if slices is not [] we should add latest
    if (slices.length === 0) {
      setValid(false);
    }
    const tempSlices = slices;
    if (tempSlices.length > 0 && tempSlices.indexOf("latest") === -1) {
      tempSlices.unshift("latest");
    }
    setValid(tempSlices.indexOf(value) > -1);

    setOptions(tempSlices.map((o: string) => ({ key: o, value: o, text: o })));
  }, [slices, value]);

  const handleChange = (
    event: SyntheticEvent<HTMLElement, Event>,
    data: DropdownProps
  ) => {
    if (data.value) {
      setValue(data.value as string);
      setValid(true);
    }
  };
  const isWeekday = (date: Date) => {
    const day = getDay(date);
    return day !== 0 && day !== 6;
  };
  return (
    <Grid columns={5} padded className={styles.picker}>
      <Grid.Column width={1}>
        <Label color={"blue"} size={"large"}>
          Date:
        </Label>
      </Grid.Column>
      <Grid.Column stretched width={2} className={styles.padded}>
        <div className={styles.top}>
          <DatePicker
            id="report-date-picker"
            className={styles.datePicker}
            maxDate={new Date()}
            selected={pickDate}
            filterDate={isWeekday}
            onChange={(selcDate: Date) => {
              if (selcDate && selcDate !== null) {
                setPickDate(selcDate);

                handleDateChange(
                  moment(selcDate).format(fileDataFormat),
                  token
                );
              }
            }}
          />
        </div>
      </Grid.Column>
      <Grid.Column width={1}>
        <Label color={"blue"} size={"large"}>
          Slice Id:
        </Label>
      </Grid.Column>
      <Grid.Column width={3} stretched className={styles.slicePicker}>
        <div className={styles.top}>
          <Dropdown
            id="report-slice-dropdown"
            onChange={handleChange}
            options={options}
            placeholder={messages.slicePlaceholder.defaultMessage}
            selection
            value={value}
          />
        </div>
      </Grid.Column>
      <Grid.Column width={4}>
        {tabIndex === 4 ? (
          <div className={styles.top}>
            <InstrumentSelector
              cusipTkrCollection={cusipTkrCollection}
              fetchInstrumentDetail={fetchInst}
              date={moment(pickDate).format(fileDataFormat)}
              slice={value}
              token={token}
              setTkrCusip={setTkrCusip}
              handleSliceChange={handleSliceChange}
              urlBase={urlBase}
            />
          </div>
        ) : (
          <div className={`column`}>
            {valid && (
              <Link
                to={`${urlBase}/${moment(pickDate).format(
                  fileDataFormat
                )}/${value}`}
              >
                <Button
                  color={"blue"}
                  onClick={() => {
                    fetchReport(
                      {
                        date: moment(pickDate).format(fileDataFormat),
                        id: value,
                      },
                      token
                    );
                    handleSliceChange({
                      date: moment(pickDate).format(fileDataFormat),
                      id: value,
                    });
                  }}
                >
                  <FormattedMessage {...messages.PickerLink} />
                </Button>
              </Link>
            )}
            {!valid && (
              <span id="no-reports">
                {" "}
                <FormattedMessage {...messages.PickerNone} />
              </span>
            )}
          </div>
        )}
      </Grid.Column>
    </Grid>
  );
};

export default ReportPicker;
